import React from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Card, Tooltip } from '@mui/material';
import { Title, Text, PieChart, PieChartGetColor } from 'shared/components';
import numeral from 'numeral';
import CardDetails from '../CardDetails';
import { SEGMENT_FILTER_FIELD } from '../../utils/cube/constants';
import { getDetails } from '../../actions';
import {
  CardContent,
  Container,
  Col,
  EmptyMessage,
  Link,
  Scroller,
  TextContainer
} from './styles';
import { TitleCard, LinkMore, TitleTotal } from '../styles';

export const BeneficiariesCard = React.memo(({
  title,
  blocks,
  emptyMessage,
  years,
  comparisonYears,
  comparisonBlocks,
  settings,
  expanded
}) => {
  const dispatch = useDispatch();
  const {accountId} = useSelector(state => state.profile.me);
  const {loading, list} = useSelector(state => state.details);
  const [openDetails, setOpenDetails] = React.useState(false);
  const isSegmmentNone = settings.segment === 'None';
  const isComparison = comparisonYears !== 'No Comparison';
  let blockBySegmentNone;
  let comparisonBySegmentNone;

  const isEmpty =
    !blocks ||
    blocks.length === 0 ||
    blocks.reduce((total, iter) => total + iter.total, 0) === 0;

  const handleOpenMoreDetails = name => {
    setOpenDetails(false);
    setTimeout(() => {
      setOpenDetails(true);
    }, 100);
    const field = SEGMENT_FILTER_FIELD[settings.segment];
    const options = { accountId };
    options[field] = name;
    dispatch(getDetails(options));
  };

  const handleCloseMoreDetails = () => {
    setOpenDetails(false);
  };

  const segmentByNone = (allBlocks) => {
    const newBlock = [{
      name: 'Beneficiaries (No Segmentation)',
      demographicId: null,
      percentage: '100%',
      total: 100,
      dataElements: []
    }];
    allBlocks.forEach(block => {
      if (block.total > 0) {
        newBlock[0].dataElements.push({
          name: block.name,
          percentage: block.percentage,
          value: block.total
        });
      }
    });
    return newBlock;
  };

  if (isSegmmentNone) {
    blockBySegmentNone = segmentByNone(blocks);
  }

  if (isSegmmentNone && isComparison) {
    comparisonBySegmentNone = segmentByNone(comparisonBlocks);
  }


  return (
    <Container expanded={expanded}>
      { ! isEmpty && (
        <TitleTotal style={{ marginBottom: 10 }}>{title}</TitleTotal>
      )}
      {isEmpty && (
        <Text centered style={{ marginBottom: 10, marginTop: 160 }}>
          {emptyMessage ||
            'No reports meet the filter criteria you have selected. Try selecting other years or other beneficiary groups.'}
        </Text>
      )}
      <YearBlocks
        blocks={isSegmmentNone ? blockBySegmentNone : blocks}
        years={years}
        comparisonYears={comparisonYears}
        openDetails={openDetails}
        comparisonBlocks={(isSegmmentNone && isComparison) ? comparisonBySegmentNone : comparisonBlocks}
        handleOpenMoreDetails={handleOpenMoreDetails}
        handleCloseMoreDetails={handleCloseMoreDetails}
        loading={loading}
        list={list}
        isSegmmentNone={isSegmmentNone}
      />
    </Container>
  );
});

const YearBlocks = React.memo(({
  blocks,
  comparisonYears,
  years,
  openDetails,
  comparisonBlocks,
  handleOpenMoreDetails,
  loading,
  list,
  handleCloseMoreDetails,
  isSegmmentNone,
}) => {


  const [ cardShowMore, setCardShowMore ] = React.useState(false);
  const [ showMore, setShowMore ] = React.useState({});
  const getAverage = block => {
    return block.dataElements && block.dataElements.length > 0
      ? 100 / block.dataElements.length
      : 0;
  };

  const renderDataElement = (block, chart) => {
    return (
      <Col key={chart.name} onClick={() => handleOpenMoreDetails(chart.name)}>
        <PieChart
          basicData={chart}
          percentage={chart.percentage}
          average={getAverage(block)}
        />
        <TextContainer>
          <Text variant="body2" centered>
            {`${numeral(chart.value).format(0, 0)}`}
          </Text>
          <Tooltip title={chart.name} arrow>
            <Box
              style={{ cursor: 'pointer', margin: 0 }}
              component="div"
              my={2}
              textOverflow="ellipsis"
              overflow="hidden"
              onClick={() => handleOpenMoreDetails(chart.name)}
            >
              <Text variant="body2" centered>{chart.name}</Text>
            </Box>
          </Tooltip>
        </TextContainer>
      </Col>
    );
  };

  const renderComparisonArea = blockName => {
    let component = null;
    if (comparisonYears && comparisonBlocks) {
      const comparisonData = comparisonBlocks.filter(
        block => block.name === blockName
      );
      if (
        comparisonData &&
        comparisonData.length === 1 &&
        comparisonData[0].dataElements.length > 0
      ) {
        const block = comparisonData[0];
        component = (
          <div>
            <Title>{comparisonYears}</Title>
            <Scroller>
              {block.dataElements.map(chart => renderDataElement(block, chart))}
            </Scroller>
          </div>
        );
      }
    }
    // eslint-disable-next-line no-restricted-globals
    if (component == null && !isNaN(comparisonYears)) {
      component = (
        <div>
          <Title>{comparisonYears}</Title>
          <Text style={{ marginBottom: 20, marginTop: 20 }}>
            No reports meet the compare criteria you&apos;ve selected.
          </Text>
        </div>
      );
    }
    return component;
  };

	const handleShowMore = (blockId) => {
		const status = !(showMore[blockId]);
		return setShowMore({[blockId]: status});
  };
  
  const cards = (cardShowMore) ? blocks : (blocks).slice(0, 5);

  return <div>
    {cards.map(
      block =>
        block.total !== 0 && (
          <Card
            key={block.name}
            variant="outlined"
            style={{ marginBottom: 10, borderRadius: 0 }}
          >
            <CardContent>
              <Link
                color="textSecondary"
              >
                { isSegmmentNone ? block.name : 
                  `${block.name} - ${numeral(block.total).format(0, 0)} (${block.percentage})`
                }
              </Link>
              {comparisonYears && years && (
                <Title>{years}</Title>
              )}
              <Scroller>
                {block.dataElements && block.dataElements.length === 0 && (
                  <EmptyMessage>
                    {`Sorry, there is no data for ${block.name} beneficiaries.`}
                  </EmptyMessage>
                )}
                {(showMore[block.name] ? block.dataElements : block.dataElements.slice(0, 6)).map(chart =>
                  renderDataElement(block, chart)
                )}
                {(block.dataElements.length > 6) && !showMore[block.name] && (
                  <LinkMore onClick={() => handleShowMore(block.name)}>Show {block.dataElements.length - 6} more...</LinkMore>
                )}
                {showMore[block.name] && (<LinkMore onClick={() => handleShowMore(block.name)}>Show Less</LinkMore>)}
              </Scroller>
              {renderComparisonArea(block.name)}
            </CardContent>
          </Card>
        )
    )}
    <LinkMore onClick={() => setCardShowMore(!cardShowMore)}>{`Show ${cardShowMore ? 'less' : 'more'}...`}</LinkMore>
    {openDetails && (
      <CardDetails
        loading={loading}
        list={list}
        onClose={handleCloseMoreDetails}
      />
    )}
  </div>;
});

BeneficiariesCard.propTypes = {
  title: PropTypes.string,
  blocks: PropTypes.array,
  emptyMessage: PropTypes.string,
  years: PropTypes.array,
  comparisonYears: PropTypes.array,
  comparisonBlocks: PropTypes.array,
  settings: PropTypes.object,
};
